/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { useLocation } from '@reach/router';
import { graphql } from 'gatsby';
import CirceWeb from '../fonts/CirceWeb-Regular.woff2';
import CirceWebBold from '../fonts/CirceWeb-Bold.woff2';
import FuturaPTBold from '../fonts/FuturaPT-Bold.woff2';
import { useSiteMetadata } from './new/useSiteMetadata';

const Head = ({ seo, meta, lang }) => {
    return (
        <Helmet
            htmlAttributes={{
                lang,
                prefix: `og: http://ogp.me/ns#`,
            }}
            title={seo.title}
            meta={[
                {
                    name: `description`,
                    content: seo.description,
                },
                {
                    property: `og:title`,
                    content: seo.title,
                },
                {
                    property: `og:image`,
                    content: `${seo.siteUrl}${seo.image}`,
                },
                {
                    property: `og:image:width`,
                    content: `600`,
                },
                {
                    property: `og:image:height`,
                    content: `315`,
                },
                {
                    property: `og:description`,
                    content: seo.description,
                },
                {
                    property: `og:type`,
                    content: seo.type,
                },
                {
                    name: `twitter:card`,
                    content: `summary_large_image`,
                },
                // {
                //     name: `twitter:creator`,
                //     content: seo.twitterUsername,
                // },
                {
                    name: `twitter:image`,
                    content: `${seo.siteUrl}${seo.image}`,
                },
                {
                    name: `twitter:title`,
                    content: seo.title,
                },
                {
                    name: `twitter:description`,
                    content: seo.description,
                },
                {
                    name: `og:url`,
                    content: seo.url,
                },
                {
                    name: `og:site_name`,
                    content: seo.siteName,
                },
            ].concat(meta)}
        >
            <link
                rel="preload"
                as="font"
                href={CirceWeb}
                type="font/woff2"
                crossOrigin="anonymous"
            />
            <link
                rel="preload"
                as="font"
                href={CirceWebBold}
                type="font/woff2"
                crossOrigin="anonymous"
            />
            <link
                rel="preload"
                as="font"
                href={FuturaPTBold}
                type="font/woff2"
                crossOrigin="anonymous"
            />
            <link rel="image_src" href={`${seo.siteUrl}${seo.image}`} />
            <link rel="canonical" href={seo.url} />
        </Helmet>
    );
};

export const SEO = ({ description, lang, meta, title, ogImage, isBlog }) => {
    const { pathname } = useLocation();

    const siteMetadata = useSiteMetadata();

    const seo = React.useMemo(() => {
        const {
            title: defaultTitle,
            titleEnding,
            description: defaultDescription,
            siteUrl,
            siteName,
            image: defaultImage,
            twitterUsername: defaultTwitterUsername,
        } = siteMetadata;

        const calcSeo = isBlog
            ? {
                  title: title ? `${title} | КЭСПА` : defaultTitle,
                  description: description || defaultDescription,
                  image: ogImage || defaultImage,
                  twitterUsername: defaultTwitterUsername,
                  url: `${siteUrl}${pathname}`,
                  siteUrl: siteUrl,
                  siteName: siteName,
                  type: `article`,
              }
            : {
                  title: title ? `${title} | ${titleEnding}` : defaultTitle,
                  description: description
                      ? `${description}. ${defaultDescription}`
                      : defaultDescription,
                  image: ogImage || defaultImage,
                  twitterUsername: defaultTwitterUsername,
                  url: `${siteUrl}${pathname}`,
                  siteUrl: siteUrl,
                  siteName: siteName,
                  type: `website`,
              };
        return calcSeo;
    }, [isBlog, siteMetadata, description, title, ogImage, pathname]);

    return <Head seo={seo} lang={lang} meta={meta} />;
};

SEO.defaultProps = {
    lang: `en`,
    meta: [],
};

SEO.propTypes = {
    description: PropTypes.string,
    lang: PropTypes.string,
    meta: PropTypes.arrayOf(PropTypes.object),
    title: PropTypes.string,
};
